import { PassiveSkillDefinition } from '@/data/types'
import { NodeState, TreeViewStateAtlas } from '@/logic/tree'
import { TreeDataProvider } from '@/logic/treeData'
import { getBundle, imagePath, imagePaths, skillIconPath } from './assets'
import { frameColor, lineState } from './renderItems'
import { Layer, RenderItem, Renderer } from './renderer'

function nodeColor(state: NodeState) {
  switch (state) {
    case 'add':
    case 'removeHistory':
      return { r: 0.8, g: 0.8, b: 0.8, a: 1.0 }
    case undefined:
      return { r: 0.6, g: 0.6, b: 0.6, a: 1.0 }
  }
}

const treeBackground = imagePath('Art/2DArt/UIImages/Common/Background2')

const atlasBackground = imagePath('Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveBackground')

const groupBackgrounds = imagePaths({
  small: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundSmall',
  smallBlank: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundSmallBlank',
  medium: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundMedium',
  mediumBlank: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundMediumBlank',
  large: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundLarge',
  largeBlank: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundLargeBlank',
  cleansingFire: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundLargeCleansingFire',
  tangle: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenGroupBackgroundLargeTangle'
})

const frameNormal = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/AtlasScreen/PassiveSkillScreenPassiveFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/AtlasScreen/PassiveSkillScreenPassiveFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/AtlasScreen/PassiveSkillScreenPassiveFrameCanAllocate'
})
const frameNotable = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenNotableFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenNotableFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenNotableFrameCanAllocate'
})
const frameKeystone = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenKeystoneFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenKeystoneFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenKeystoneFrameCanAllocate'
})
const frameWormhole = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasWormholeFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasWormholeFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasWormholeFrameCanAllocate'
})
const iconWormhole = imagePaths({
  active: 'Art/2DArt/SkillIcons/passives/AtlasTrees/AtlasPassiveWormholeActiveIcon',
  default: 'Art/2DArt/SkillIcons/passives/AtlasTrees/AtlasPassiveWormholeDefaultIcon'
})

const lineTextures = imagePaths({
  normal: 'Art/2DArt/PassiveTree/AtlasPassiveSkillScreenCurvesNormalBlueTogether',
  active: 'Art/2DArt/PassiveTree/AtlasPassiveSkillScreenCurvesActiveBlueTogether',
  intermediate: 'Art/2DArt/PassiveTree/AtlasPassiveSkillScreenCurvesIntermediateBlueTogether',
  ornament: 'Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenOrnament1'
})
const lineColors = {
  normal: 0x141c1e,
  active: 0x4c7aa4,
  intermediate: 0x49676e
}

const startNode = imagePath('Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveSkillScreenStart')
const masteryHighlight = imagePath('Art/2DArt/UIImages/InGame/AtlasScreen/AtlasPassiveMasteryIconHighlight')

function nodeSize(skill: PassiveSkillDefinition) {
  if (skill.is_notable) {
    return 48
  } else if (skill.is_keystone) {
    return 84
  } else if (skill.is_just_icon) {
    return 0
  } else {
    return 26
  }
}

export function renderAtlasTree(data: TreeDataProvider) {
  const items: RenderItem[] = []
  const tree = data.state as TreeViewStateAtlas

  if (!tree.hideBackground) {
    items.push({
      layer: Layer.BACKGROUND,
      image: treeBackground,
      type: 'background'
    })
  }

  items.push({
    layer: Layer.ARTWORK,
    image: atlasBackground,
    type: 'image',
    x: -62,
    y: -4620,
    centered: true,
    scale: 2.82
  })
  items.push({
    layer: Layer.GROUP,
    image: groupBackgrounds.cleansingFire,
    type: 'image',
    x: -3733.75 + 3,
    y: -7767.25 - 14,
    centered: true
  })
  items.push({
    layer: Layer.GROUP,
    image: groupBackgrounds.tangle,
    type: 'image',
    x: -62 + 3786,
    y: -4620 - 3207,
    centered: true
  })

  for (const id of Object.keys(data.tree.groups)) {
    const group = data.group(Number(id))
    if (!group) continue
    if (group.bg === 1) {
      items.push({
        layer: Layer.GROUP,
        image: groupBackgrounds.small,
        type: 'image',
        x: group.x,
        y: group.y,
        centered: true
      })
    } else if (group.bg === 2) {
      items.push({
        layer: Layer.GROUP,
        image: groupBackgrounds.medium,
        type: 'image',
        x: group.x,
        y: group.y,
        centered: true
      })
    } else if (group.bg === 3) {
      items.push({
        layer: Layer.GROUP,
        image: groupBackgrounds.large,
        type: 'image',
        x: group.x,
        y: group.y,
        centered: true
      })
    } else if (group.bg === 5) {
      items.push({
        layer: Layer.GROUP,
        image: groupBackgrounds.smallBlank,
        type: 'image',
        x: group.x,
        y: group.y,
        centered: true
      })
    }
  }

  for (const id of data.allNodes()) {
    const node = data.node(id)!
    const skill = data.nodeSkill(id)!

    if (id === data.tree.root_passives[0]) {
      items.push({
        layer: Layer.NODE_FRAME,
        image: startNode,
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true
      })
    } else if (skill.is_just_icon) {
      items.push({
        layer: Layer.MASTERY_BACKGROUND,
        image: skillIconPath(skill.icon),
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true
      })
      const active = node.group.nodes.find((id) => {
        const node = data.node(id)
        return node && node.skill.is_notable && node.active
      })
      if (active != null) {
        items.push({
          layer: Layer.MASTERY_ICON,
          image: masteryHighlight,
          type: 'image',
          x: node.x,
          y: node.y,
          centered: true
        })
      }
    } else if (skill.is_wormhole) {
      items.push({
        layer: Layer.NODE_ICON + Layer.ASCENDANCY,
        image: node.active ? iconWormhole.active : iconWormhole.default,
        color: nodeColor(node.state),
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true,
        size: 172
      })
      items.push({
        layer: Layer.NODE_FRAME + Layer.ASCENDANCY,
        image: node.state ? frameWormhole.active : frameWormhole.normal,
        color: frameColor(node.state),
        type: 'image',
        interactKey: id,
        x: node.x,
        y: node.y,
        centered: true
      })
    } else {
      items.push({
        layer: Layer.NODE_ICON,
        image: skillIconPath(skill.icon),
        color: nodeColor(node.state),
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true,
        size: skill.is_notable ? 98 : skill.is_keystone ? 128 : 64
      })
      const frames = skill.is_notable ? frameNotable : skill.is_keystone ? frameKeystone : frameNormal
      items.push({
        layer: Layer.NODE_FRAME,
        image: node.state ? frames.active : frames.normal,
        color: frameColor(node.state),
        type: 'image',
        interactKey: id,
        x: node.x,
        y: node.y,
        centered: true
      })
    }

    for (const id2 of node.connections) {
      if (id2 <= id) continue
      const node2 = data.node(id2)
      if (!node2) continue
      const skill2 = node2.skill
      if (skill.is_just_icon || skill2.is_just_icon) continue

      const wormholeLine = skill.is_wormhole && skill2.is_wormhole
      if (wormholeLine && tree.hover !== id && tree.hover !== id2) continue

      const state = lineState(node.state, node2.state)
      const color = wormholeLine ? { r: 1.0, g: 0.7, b: 0.0, a: 1.0 } : frameColor(state)
      const flatColor = state ? lineColors.active : lineColors.normal
      const image = wormholeLine ? lineTextures.active : state ? lineTextures.active : lineTextures.normal
      const layer = Layer.LINE + (wormholeLine ? Layer.ASCENDANCY : 0)
      const layerOrnament = Layer.LINE_ORNAMENT + (wormholeLine ? Layer.ASCENDANCY : 0)

      if (node.group === node2.group && node.radius === node2.radius) {
        items.push({
          layer,
          image,
          color,
          flatColor,
          type: 'arc',
          x: node.group.x,
          y: node.group.y,
          r: node.radius,
          a1: node.arc,
          a2: node2.arc
        })
      } else {
        items.push({
          layer,
          image,
          color,
          flatColor,
          type: 'line',
          x1: node.x,
          y1: node.y,
          x2: node2.x,
          y2: node2.y
        })
        if (!wormholeLine) {
          const dx = node2.x - node.x
          const dy = node2.y - node.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          const angle = Math.atan2(dy, dx)
          const r1 = nodeSize(skill) + 24
          const r2 = nodeSize(skill2) + 24
          items.push({
            layer: layerOrnament,
            image: lineTextures.ornament,
            color,
            type: 'image',
            x: node.x + (dx * r1) / dist,
            y: node.y + (dy * r1) / dist,
            angle: angle,
            centered: true
          })
          items.push({
            layer: layerOrnament,
            image: lineTextures.ornament,
            color,
            type: 'image',
            x: node2.x - (dx * r2) / dist,
            y: node2.y - (dy * r2) / dist,
            angle: angle + Math.PI,
            centered: true
          })
        }
      }
    }
  }

  if (tree.highlight) {
    for (const id of tree.highlight) {
      const node = data.node(id)
      if (!node) continue

      let size = node.skill.is_keystone || node.skill.is_wormhole ? 256 : node.skill.is_notable ? 192 : 128

      items.push({
        layer: Layer.NODE_HIGHLIGHT + (node.skill.is_wormhole ? Layer.ASCENDANCY : 0),
        image: treeBackground, // neutral image that's always loaded
        type: 'circle',
        x: node.x,
        y: node.y,
        r: size / 2,
        color: { r: 0, g: 1, b: 0, a: 1 }
      })
    }
  }

  return items
}

export function loadAtlasTree(tree: TreeViewStateAtlas, renderer: Renderer) {
  const bundles = ['atlas_passive_tree']
  if (bundles.every((name) => renderer.loaded.has(name))) return undefined
  const promises: Promise<any>[] = bundles.map((name) =>
    getBundle(name).then((bundle) => renderer.loadBundle(name, bundle))
  )
  return Promise.all(promises)
}
